import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import {
  PlannedFood,
  PlannedFoodPortion,
} from "@/components/FoodDatabase/utils";
import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
} from "@/config/constants";
import { MealPatternItemAttributes, MealPatternNameKind } from "@/gql/graphql";
import { getPortionWeightGrams } from "@/utils/food";
import { round } from "@/utils/number";
import { pluralize } from "@/utils/string";

import { Cell } from "./Cell";

export default class MealPatternItem {
  uuid: string;
  name: MealPatternNameKind;
  amountServings: number;
  food: PlannedFood;
  foodPortion: PlannedFoodPortion;
  cell: Cell;

  get weightGrams(): number {
    return getPortionWeightGrams(
      {
        amount: this.foodPortion?.amount ?? 0,
        weightGrams: this.foodPortion?.weightGrams ?? 0,
      },
      this.amountServings
    );
  }

  get weightPercent(): number {
    return this.weightGrams / (this.food?.epWeightGrams ?? 0);
  }

  get energyKcal(): number {
    return (this.food?.energyKcal ?? 0) * this.weightPercent;
  }

  // Macros scale by weightPercent, the same basis as energyKcal above — the
  // food's nutrient values are stored per epWeightGrams (see PlannedMealItem,
  // which scales everything the same way). Previously these scaled by
  // amountServings, so the grams didn't reconcile with the calories.
  get carbohydrateGrams(): number {
    return (this.food?.carbohydrateGrams ?? 0) * this.weightPercent;
  }

  get carbohydratePercent(): number {
    return (
      (this.carbohydrateGrams * CALORIES_PER_CARBOHYDRATE_GRAM) /
      this.energyKcal
    );
  }

  get proteinGrams(): number {
    return (this.food?.proteinGrams ?? 0) * this.weightPercent;
  }

  get proteinPercent(): number {
    return (this.proteinGrams * CALORIES_PER_PROTEIN_GRAM) / this.energyKcal;
  }

  get fatGrams(): number {
    return (this.food?.fatGrams ?? 0) * this.weightPercent;
  }

  get fatPercent(): number {
    return (this.fatGrams * CALORIES_PER_FAT_GRAM) / this.energyKcal;
  }

  get amountWithUnit(): string {
    return `${round(this.amountServings, 2)} ${pluralize(
      "serving",
      this.amountServings
    )}`;
  }

  constructor(
    cell: Cell,
    name: MealPatternNameKind,
    amountServings: number,
    food: MealPatternItem["food"] | undefined | null,
    foodPortion: MealPatternItem["foodPortion"],
    uuid = uuidv4()
  ) {
    makeAutoObservable(this);
    this.cell = cell;
    this.name = name;
    this.amountServings = amountServings;
    this.food = food;
    this.foodPortion = foodPortion;
    this.uuid = uuid;
  }

  incrementAmount(): void {
    this.setAmount(this.amountServings + 1);
  }

  decrementAmount(): void {
    this.setAmount(this.amountServings - 1);
  }

  setAmount(value: number): void {
    this.amountServings = value;
    if (this.amountServings <= 0) this.remove();
  }

  remove(): void {
    this.cell.removeMealPatternItem(this);
  }

  toAttributes(): MealPatternItemAttributes {
    return {
      uuid: this.uuid,
      name: this.name,
      amountServings: this.amountServings,
      foodId: this.food?.id,
      foodPortionId: this.foodPortion?.id,
    };
  }
}
